import React, { Key, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import './TransactionsList.scss'

import filterIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/filter.svg'
import exportIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/export.svg'
import arrowLeft from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/arrow-left.png'
import arrowRight from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/arrow-right.svg'
import transactionService from 'Services/transactionService'
import { CurrencyFormatter, checkSearchTransactionInputType } from 'Helpers'
import { transactionBXService } from 'BoostXpressAdmin/Services/transactionBXService'
import { TransactionXModel } from 'BoostXpressAdmin/Model/transactionXModel'
import { HorizontalLoader } from 'Components'
import { DateTimeFormatter } from 'Helpers/DateTimeFormatter'

type TransactionStatus = 'Success' | 'Pending' | 'Failed'

type PagedDatasetResponse<T> = {
  data: {
    data: T[]
    meta: {
      pagination: {
        total: number
        count: number
        per_page: number
        current_page: number
        total_pages: number
        links: {
          next: string
        }
      }
    }
  }
}

const TransactionsList = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [transactions, setTransactions] = useState<TransactionXModel[]>([])

  const [searchTerm, setSearchTerm] = useState('')
  const [searchEndpoint, setSearchEndpoint] = useState<string>('')

  useEffect(() => {
    const userResId = searchParams.get('userResourceId')
    if (userResId) {
      setSearchTerm(userResId)
    }
  }, [searchParams])

  // Filter states
  const [showFilters, setShowFilters] = useState(false)
  const [transactionType, setTransactionType] = useState('')
  const [paymentStatus, setPaymentStatus] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const status = 'ALL'
  const sortOrder: 'ASC' | 'DESC' = 'DESC'
  const flowType = ''
  const includePOSTransactions = undefined

  useEffect(() => {
    const t = window.setTimeout(() => {
      const trimmed = searchTerm.trim()
      if (!trimmed) {
        setSearchEndpoint('')
        setPageNumber(1)
        return
      }

      const endpoint = `userResourceId=${encodeURIComponent(trimmed)}`

      setSearchEndpoint(endpoint)
      setPageNumber(1)
    }, 350)

    return () => window.clearTimeout(t)
  }, [searchTerm])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setIsLoading(true)

        // Build filter parameters
        const params: any = {
          page: pageNumber,
          limit: 50,
          sortOrder: 'DESC',
        }

        // Add search endpoint if exists
        if (searchEndpoint) {
          const searchParams = searchEndpoint.split('&')
          searchParams.forEach((param) => {
            const [key, value] = param.split('=')
            params[key] = value
          })
        }

        // Add filters
        if (transactionType) params.transactionType = transactionType
        if (paymentStatus) params.paymentStatus = paymentStatus
        if (startDate) params['startDateYYYY_MM_DD'] = startDate
        if (endDate) params['endDateYYYY_MM_DD'] = endDate

        const res = (await transactionBXService.index(
          params,
        )) as unknown as PagedDatasetResponse<TransactionXModel>
        if (cancelled) return
        setTransactions(res.data.data ?? [])
        setTotal(res.data.meta.pagination.total ?? 0)
        setPageSize(res.data.meta.pagination.per_page ?? 20)
      } catch (e) {
        if (!cancelled) {
          setTransactions([])
          setTotal(0)
          setPageSize(20)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [
    flowType,
    includePOSTransactions,
    pageNumber,
    searchEndpoint,
    sortOrder,
    status,
    transactionType,
    paymentStatus,
    startDate,
    endDate,
  ])

  const totalPages = useMemo(() => {
    if (!total) return 1
    return Math.max(1, Math.ceil(total / (pageSize || 20)))
  }, [pageSize, total])

  const mapStatus = (paidStatus?: string): TransactionStatus => {
    const s = (paidStatus ?? '').toLowerCase()
    if (s.includes('pending')) return 'Pending'
    if (s.includes('success') || s.includes('paid') || s.includes('completed')) return 'Success'
    return 'Failed'
  }

  const safeParseMeta = (meta: string) => {
    try {
      return JSON.parse(meta) as Record<string, unknown>
    } catch (e) {
      return {}
    }
  }

  const handleExport = async () => {
    try {
      setIsExporting(true)
      const params: any = {
        sortOrder: 'DESC',
      }

      if (searchEndpoint) {
        const searchParams = searchEndpoint.split('&')
        searchParams.forEach((param) => {
          const [key, value] = param.split('=')
          params[key] = value
        })
      }

      if (transactionType) params.transactionType = transactionType
      if (paymentStatus) params.paymentStatus = paymentStatus
      if (startDate) params['startDateYYYY_MM_DD'] = startDate
      if (endDate) params['endDateYYYY_MM_DD'] = endDate

      const response = await transactionBXService.exportCSV(params)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `transactions-${new Date().getTime()}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error('Export failed', error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className='bx-transactions-list'>
      <div className='bx-transactions-list__header'>
        <h2 className='bx-transactions-list__title'>Transactions Management</h2>
      </div>

      <div className='bx-transactions-list__content'>
        <div className='bx-transactions-list__toolbar row g-2 align-items-center'>
          <div className='col-12 col-md-8 col-lg-10'>
            <div className='bx-transactions-search w-100'>
              <div className='bx-transactions-search__icon'>
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                    stroke='#868484'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <input
                type='text'
                className='bx-transactions-search__input'
                placeholder='Search by userResource ID'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className='col-6 col-md-2 col-lg-1 position-relative'>
            <button
              className='bx-transactions-actions__btn w-100'
              onClick={() => setShowFilters(!showFilters)}
            >
              <img src={filterIcon} alt='' className='bx-transactions-actions__icon' />
              <span className='bx-transactions-actions__label'>Filters</span>
            </button>

            {showFilters && (
              <div className='bx-transactions-filters-dropdown'>
                <div className='bx-filters-header'>
                  <h5>Filter Transactions</h5>
                  <button className='bx-filters-close' onClick={() => setShowFilters(false)}>
                    ×
                  </button>
                </div>

                <div className='bx-filters-body'>
                  <div className='bx-filter-group'>
                    <label>Transaction Type</label>
                    <select
                      value={transactionType}
                      onChange={(e) => setTransactionType(e.target.value)}
                      className='form-control'
                    >
                      <option value=''>All Types</option>
                      <option value='external_to_fund_wallet'>External to Fund Wallet</option>
                      <option value='payment_for_order'>Payment for Order</option>
                      <option value='payment_for_voucher'>Payment for Voucher</option>
                      <option value='voucher_redeem'>Voucher Redeem</option>
                      <option value='wallet_funds_withdrawal'>Wallet Funds Withdrawal</option>
                      <option value='wallet_balance_refund'>Wallet Balance Refund</option>
                      <option value='referral_bonus'>Referral Bonus</option>
                      <option value='welcome_bonus'>Welcome Bonus</option>
                      <option value='credit_by_admin'>Credit by Admin</option>
                      <option value='debit_by_admin'>Debit by Admin</option>
                      <option value='bnpm_promo_refund'>BNPM promo refund</option>
                    </select>
                  </div>

                  <div className='bx-filter-group'>
                    <label>Payment Status</label>
                    <select
                      value={paymentStatus}
                      onChange={(e) => setPaymentStatus(e.target.value)}
                      className='form-control'
                    >
                      <option value=''>All Status</option>
                      <option value='unpaid'>Unpaid</option>
                      <option value='paid'>Paid</option>
                      <option value='failed'>Failed</option>
                      <option value='pending'>Pending</option>
                      <option value='refunded'>Refunded</option>
                    </select>
                  </div>

                  <div className='bx-filter-group'>
                    <label>Start Date</label>
                    <input
                      type='date'
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className='form-control'
                    />
                  </div>

                  <div className='bx-filter-group'>
                    <label>End Date</label>
                    <input
                      type='date'
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className='form-control'
                    />
                  </div>
                </div>

                <div className='bx-filters-footer'>
                  <button
                    className='btn btn-secondary'
                    onClick={() => {
                      setTransactionType('')
                      setPaymentStatus('')
                      setStartDate('')
                      setEndDate('')
                    }}
                  >
                    Clear Filters
                  </button>
                  <button className='btn btn-primary' onClick={() => setShowFilters(false)}>
                    Apply Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className='col-6 col-md-2 col-lg-1'>
            <button
              className='bx-transactions-actions__btn w-100'
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <span className='bx-transactions-actions__label'>Exporting...</span>
              ) : (
                <>
                  <img src={exportIcon} alt='' className='bx-transactions-actions__icon' />
                  <span className='bx-transactions-actions__label'>Export</span>
                </>
              )}
            </button>
          </div>
        </div>

        <div className='bx-transactions-table'>
          <table className='bx-transactions-table__table'>
            <thead>
              <tr>
                <th className='bx-transactions-table__th bx-transactions-table__th--id'>S/N</th>
                <th className='bx-transactions-table__th bx-transactions-table__th--user'>User</th>

                <th className='bx-transactions-table__th bx-transactions-table__th--amount'>
                  Amount
                </th>
                <th className='bx-transactions-table__th bx-transactions-table__th--date'>Date</th>
                <th className='bx-transactions-table__th bx-transactions-table__th--status'>
                  Status
                </th>
                <th className='bx-transactions-table__th bx-transactions-table__th--type'>Type</th>
                <th className='bx-transactions-table__th bx-transactions-table__th--actions'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className='bx-transactions-table__td' colSpan={8}>
                    <HorizontalLoader />
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td className='bx-transactions-table__td' colSpan={8}>
                    No transactions found.
                  </td>
                </tr>
              ) : (
                transactions.map((t: TransactionXModel, index: Key) => {
                  const amountLabel = `${t.currency ?? '₦'}${CurrencyFormatter(Number(t.amountMajor ?? 0))}`
                  const dateLabel = t.createdAt ? DateTimeFormatter(t.createdAt) : '-'

                  return (
                    <tr key={t.resourceId}>
                      <td className='bx-transactions-table__td bx-transactions-table__td--id'>
                        {Number(index) + 1 + (pageNumber - 1) * pageSize}
                      </td>
                      <td className='bx-transactions-table__td bx-transactions-table__td--user'>
                        {t.user.firstName ?? ''} {t.user.lastName ?? ''}
                      </td>

                      <td className='bx-transactions-table__td bx-transactions-table__td--amount'>
                        {amountLabel}
                      </td>
                      <td className='bx-transactions-table__td bx-transactions-table__td--date'>
                        {dateLabel}
                      </td>
                      <td className='bx-transactions-table__td bx-transactions-table__td--status'>
                        <div className='bx-transactions-table__badge-wrap'>
                          <span
                            className={`bx-transactions-table__badge bx-transactions-table__badge--${t.paymentStatus.toLowerCase()}`}
                          >
                            {t.paymentStatus}
                          </span>
                        </div>
                      </td>
                      <td className='bx-transactions-table__td bx-transactions-table__td--status'>
                        <div className='bx-transactions-table__badge-wrap'>{t.transactionType}</div>
                      </td>
                      <td className='bx-transactions-table__td bx-transactions-table__td--actions'>
                        <button
                          className='bx-transactions-table__view'
                          onClick={() => navigate(`/boostxpress/transactions/${t.resourceId}`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>

          <div className='bx-transactions-pagination'>
            <button
              className='bx-transactions-pagination__btn'
              disabled={pageNumber <= 1 || isLoading}
              onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            >
              <div className='bx-transactions-pagination__btn-icon-wrap'>
                <img
                  src={arrowLeft}
                  alt=''
                  className='bx-transactions-pagination__btn-icon bx-transactions-pagination__btn-icon--left'
                />
              </div>
              <span className='bx-transactions-pagination__btn-text'>Previous</span>
            </button>

            <div className='bx-transactions-pagination__numbers'>
              <span className='bx-transactions-pagination__number bx-transactions-pagination__number--active'>
                {pageNumber}
              </span>
              <span className='bx-transactions-pagination__ellipsis'>/</span>
              <span className='bx-transactions-pagination__number'>{totalPages}</span>
            </div>

            <button
              className='bx-transactions-pagination__btn'
              disabled={pageNumber >= totalPages || isLoading}
              onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
            >
              <span className='bx-transactions-pagination__btn-text'>Next</span>
              <img src={arrowRight} alt='' className='bx-transactions-pagination__btn-icon' />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TransactionsList
